import React from 'react'
import {GridColDef} from '@mui/x-data-grid'
import {Box, Chip, Tooltip} from '@mui/material'
import {CsvRowOutcome} from 'models/CsvValidationSummary'

const validationRowColumns: GridColDef[] = [
  {field: 'rowNumber', headerName: 'Row', width: 80},
  {
    field: 'status',
    headerName: 'Status',
    width: 120,
    renderCell: params => {
      const row = params.row as CsvRowOutcome
      return row.status === 'error' ? (
        <Chip label="Error" color="error" size="small" />
      ) : (
        <Chip label="Warning" color="warning" size="small" />
      )
    },
  },
  {
    field: 'message',
    headerName: 'Reason',
    flex: 2,
    // A row can carry more than one distinct issue (e.g. two missing columns), joined by the
    // backend with '\n' — one bullet per issue. The row height is fixed (index.tsx), so this
    // list fills that fixed height and scrolls internally rather than growing the row — an
    // issue is either fully visible or reachable by scrolling, never silently clipped.
    renderCell: params => {
      const message = (params.row as CsvRowOutcome).message ?? ''
      const lines = message.split('\n').filter(Boolean)
      if (lines.length <= 1) {
        return (
          <Tooltip title={message}>
            <span style={{overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'}}>
              {message}
            </span>
          </Tooltip>
        )
      }
      return (
        <Box
          component="ul"
          sx={{
            margin: 0,
            paddingLeft: 2.5,
            paddingY: 0.5,
            width: '100%',
            height: '100%',
            boxSizing: 'border-box',
            overflowY: 'auto',
            // Without this, scrolling past the top/bottom of this list "chains" into the outer
            // DataGrid's own scroll (used for many rows/columns) — contain keeps the wheel
            // gesture scoped to this list only.
            overscrollBehavior: 'contain',
          }}>
          {lines.map((line, index) => (
            // Index is safe here — order is stable per render — unlike the line text, which can
            // repeat across two identical issues and break React's key uniqueness.
            <li key={index}>
              <Tooltip title={line}>
                <span
                  style={{
                    display: 'block',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}>
                  {line}
                </span>
              </Tooltip>
            </li>
          ))}
        </Box>
      )
    },
  },
  {
    field: 'patientName',
    headerName: 'Patient',
    flex: 1,
    valueGetter: params => (params.row as CsvRowOutcome).patientName ?? '--',
  },
  {
    field: 'deviceSerial',
    headerName: 'Device Serial',
    flex: 1,
    valueGetter: params => (params.row as CsvRowOutcome).deviceSerial ?? '--',
  },
]

export default validationRowColumns
